import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { NAV, CONFIG, waLink } from "../data/content";
import { EASE } from "./Reveal";

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 1.1, ease: EASE, delay: 0.2 }}
      className="fixed top-4 sm:top-6 inset-x-0 mx-auto z-50 w-[92%] max-w-7xl"
      data-testid="navbar"
    >
      <div
        className={`flex items-center justify-between rounded-full pl-6 sm:pl-8 pr-2.5 py-2.5 sm:py-3 border shadow-[0_10px_40px_-16px_rgba(20,4,8,0.7)] transition-colors duration-500 ${
          scrolled
            ? "bg-[#21070D]/80 border-[#EBD08A]/15 backdrop-blur-lg"
            : "bg-[#2A0A12]/30 border-[#F4EBD9]/15 backdrop-blur-md"
        }`}
      >
        <a href="#top" className="flex flex-col leading-none shrink-0" data-testid="nav-logo">
          <span className="font-serif text-lg sm:text-xl text-[#F4EBD9] tracking-wide">
            {CONFIG.brand}
          </span>
          <span className="text-[9px] sm:text-[10px] uppercase tracking-[0.3em] text-[#D9B24A]">
            {CONFIG.tagline}
          </span>
        </a>

        <nav className="hidden lg:flex items-center gap-6 lg:gap-7">
          {NAV.map((n) => (
            <a
              key={n.href}
              href={n.href}
              target={n.external ? "_blank" : undefined}
              rel={n.external ? "noreferrer" : undefined}
              data-testid={`nav-${n.label.toLowerCase()}`}
              className="text-xs uppercase tracking-[0.16em] whitespace-nowrap text-[#F4EBD9]/80 hover:text-[#EBD08A] transition-colors duration-300"
            >
              {n.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-2 shrink-0">
          <a
            href={waLink()}
            target="_blank"
            rel="noreferrer"
            data-testid="nav-book-btn"
            className="btn-gold hidden sm:flex flex-col items-center leading-none whitespace-nowrap rounded-full px-6 py-2 font-semibold"
          >
            <span className="text-sm uppercase tracking-[0.14em]">Book</span>
            <span className="text-[9px] uppercase tracking-[0.18em] opacity-80 mt-0.5">
              Consultations Here
            </span>
          </a>
          <button
            onClick={() => setOpen((o) => !o)}
            className="lg:hidden grid place-items-center h-10 w-10 rounded-full bg-white/10 text-[#F4EBD9]"
            data-testid="nav-mobile-toggle"
            aria-label="Menu"
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.4, ease: EASE }}
            className="lg:hidden mt-3 rounded-3xl bg-[#21070D]/95 border border-white/10 backdrop-blur-xl p-6 flex flex-col gap-4"
          >
            {NAV.map((n) => (
              <a
                key={n.href}
                href={n.href}
                target={n.external ? "_blank" : undefined}
                rel={n.external ? "noreferrer" : undefined}
                onClick={() => setOpen(false)}
                className="text-sm uppercase tracking-[0.2em] text-[#F4EBD9]/85 hover:text-[#EBD08A]"
                data-testid={`nav-mobile-${n.label.toLowerCase()}`}
              >
                {n.label}
              </a>
            ))}
            <a
              href={waLink()}
              target="_blank"
              rel="noreferrer"
              className="btn-gold mt-2 text-center rounded-full px-6 py-3 text-xs uppercase tracking-[0.2em] font-semibold"
            >
              Book Consultations Here
            </a>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
